import {
  MdMenuBook,
  MdScience,
  MdCalculate,
  MdPublic,
  MdMusicNote,
  MdBrush,
  MdPsychology,
  MdCode,
  MdAdd,
  MdClose
} from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import UserProfile from '../../components/dashboard/UserProfile';
import SubjectTabs from '../../components/dashboard/SubjectTabs';
import SubjectCard from '../../components/dashboard/SubjectCard';
import ParentDashboard from '../../components/dashboard/ParentDashboard';
import { EnrolledSubject } from '../../types/subjects';
import { useAuth } from '../../contexts/AuthContext';
import { lessonAPI } from '../../utils/api';
import { StartLessonResponse } from '../../types/lesson';
import ModeSelectionModal, { LessonMode } from '../../components/walkthrough/ModeSelectionModal';
import { VOICE_MODE_ENABLED } from '../../config/features';
import WalkthroughTour from '../../components/walkthrough/WalkthroughTour';
import {
  dashboardNoSubjectsSteps,
  dashboardWithSubjectsSteps,
  childNoSubjectsSteps,
  childWithSubjectsSteps,
  parentNoChildrenSteps,
  parentWithChildrenSteps
} from '../../components/walkthrough/tourSteps';
import { shouldShowTour } from '../../config/tourConfig';
import TrialBanner from '../../components/subscription/TrialBanner';
import PremiumFeatureLock from '../../components/subscription/PremiumFeatureLock';
import { useSubscription } from '../../hooks/useSubscription';
import { profileAPI } from '../../utils/api';

const BookOpen = MdMenuBook as any;
const Flask = MdScience as any;
const Calculator = MdCalculate as any;
const Globe = MdPublic as any;
const MusicNote = MdMusicNote as any;
const PaintBrush = MdBrush as any;
const Brain = MdPsychology as any;
const Atom = MdScience as any;
const Code = MdCode as any;
const Plus = MdAdd as any;
const X = MdClose as any;

const iconMap = {
  BookOpen,
  Flask,
  Calculator,
  Globe,
  MusicNote,
  PaintBrush,
  Brain,
  Atom,
  Code,
};

// Map subject names to icons
const getIconForSubject = (subjectName: string): string => {
  const normalizedName = subjectName.toLowerCase();
  
  if (normalizedName.includes('literature') || normalizedName.includes('english') || normalizedName.includes('reading')) return 'BookOpen';
  if (normalizedName.includes('chemistry')) return 'Flask';
  if (normalizedName.includes('math') || normalizedName.includes('algebra') || normalizedName.includes('calculus')) return 'Calculator';
  if (normalizedName.includes('geography') || normalizedName.includes('history')) return 'Globe';
  if (normalizedName.includes('music')) return 'MusicNote';
  if (normalizedName.includes('art') || normalizedName.includes('design')) return 'PaintBrush';
  if (normalizedName.includes('psychology') || normalizedName.includes('psych')) return 'Brain';
  if (normalizedName.includes('physics') || normalizedName.includes('science')) return 'Atom';
  if (normalizedName.includes('programming') || normalizedName.includes('computer') || normalizedName.includes('coding')) return 'Code';
  
  // Default icon
  return 'BookOpen';
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, refreshProfile } = useAuth();
  const { courseLimit, isActive, isTrialExpired } = useSubscription();
  const [trialExpiredFeature, setTrialExpiredFeature] = useState<string | null>(null);
  const [enrolledSubjects, setEnrolledSubjects] = useState<EnrolledSubject[]>([]);
  const [hasChildren, setHasChildren] = useState(false);

  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  console.log(`[Dashboard] Component render #${renderCountRef.current}`);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [isNoSubjectsModalOpen, setIsNoSubjectsModalOpen] = useState(false);
  const [isCourseLimitModalOpen, setIsCourseLimitModalOpen] = useState(false);
  const [archivedSubject, setArchivedSubject] = useState<{ id: string; name: string } | null>(null);
  const [deleteSubjectModal, setDeleteSubjectModal] = useState<{ id: string; name: string } | null>(null);
  const [isDeletingSubject, setIsDeletingSubject] = useState(false);
  const [pendingModeForLesson, setPendingModeForLesson] = useState<LessonMode | null>(null);
  const [showSetDefaultModal, setShowSetDefaultModal] = useState(false);
  const [isStartingLesson, setIsStartingLesson] = useState(false);
  const [audioDeviceModal, setAudioDeviceModal] = useState<{
    show: boolean;
    type: 'microphone' | 'speaker' | 'both';
    message: string;
    selectedSubject?: EnrolledSubject; // Store the selected subject when audio check fails
  }>({
    show: false,
    type: 'microphone',
    message: '',
    selectedSubject: undefined
  });

  // Tour state - determines which contextual tour to show
  const [activeTourSection, setActiveTourSection] = useState<'dashboard-no-subjects' | 'dashboard-with-subjects' | 'child-no-subjects' | 'child-with-subjects' | 'parent-no-children' | 'parent-with-children' | null>(null);

  // Mode selection state
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [selectedSubjectForLesson, setSelectedSubjectForLesson] = useState<EnrolledSubject | null>(null);
  const [audioAvailable, setAudioAvailable] = useState(true);

  useEffect(() => {
    if (userProfile?.subjects) {
      // Convert API subjects to enrolled subjects format
      const enrolled: EnrolledSubject[] = userProfile.subjects.map((subject: any) => ({
        id: subject.id.toString(),
        name: subject.name,
        icon: getIconForSubject(subject.name),
        is_archived: subject.is_archived || false,
      }));
      setEnrolledSubjects(enrolled);
    }
  }, [userProfile]);

  // Fetch children count for parent accounts
  useEffect(() => {
    const fetchChildrenCount = async () => {
      if (userProfile?.account_type === 'parent') {
        try {
          const { parentAPI } = await import('../../utils/api');
          const response = await parentAPI.listChildren();
          setHasChildren(response.children.length > 0);
        } catch (error) {
          console.error('Failed to fetch children count:', error);
          setHasChildren(false);
        }
      }
    };

    fetchChildrenCount();
  }, [userProfile?.account_type]);

  // Determine which tour section to show based on user state and account type
  useEffect(() => {
    if (!userProfile) return;

    const accountType = userProfile.account_type || 'student';
    const hasSubjects = (userProfile.subjects?.length ?? 0) > 0;

    console.log('[Dashboard] Tour state check:', {
      accountType,
      hasSubjects,
      hasChildren,
      tour_enabled: userProfile.tour_enabled,
      tour_dashboard_no_subjects: userProfile.tour_dashboard_no_subjects,
      tour_dashboard_with_subjects: userProfile.tour_dashboard_with_subjects
    });

    // Check tours with testing flag support
    // Parent account tours
    if (accountType === 'parent') {
      // Show "add child" tour first
      if (shouldShowTour(userProfile.tour_dashboard_no_subjects, userProfile.tour_enabled, 'parent-no-children')) {
        console.log('[Dashboard] Setting tour to: parent-no-children');
        setActiveTourSection('parent-no-children');
      }
      // Only show "manage subjects" tour if they actually have children
      else if (hasChildren && shouldShowTour(userProfile.tour_dashboard_with_subjects, userProfile.tour_enabled, 'parent-with-children')) {
        console.log('[Dashboard] Setting tour to: parent-with-children');
        setActiveTourSection('parent-with-children');
      } else {
        setActiveTourSection(null);
      }
    }
    // Child account tours
    else if (accountType === 'child') {
      if (!hasSubjects && shouldShowTour(userProfile.tour_dashboard_no_subjects, userProfile.tour_enabled, 'child-no-subjects')) {
        console.log('[Dashboard] Setting tour to: child-no-subjects');
        setActiveTourSection('child-no-subjects');
      } else if (hasSubjects && shouldShowTour(userProfile.tour_dashboard_with_subjects, userProfile.tour_enabled, 'child-with-subjects')) {
        console.log('[Dashboard] Setting tour to: child-with-subjects');
        setActiveTourSection('child-with-subjects');
      } else {
        setActiveTourSection(null);
      }
    }
    // Student account tours (default)
    else {
      if (!hasSubjects && shouldShowTour(userProfile.tour_dashboard_no_subjects, userProfile.tour_enabled, 'dashboard-no-subjects')) {
        console.log('[Dashboard] Setting tour to: dashboard-no-subjects');
        setActiveTourSection('dashboard-no-subjects');
      } else if (hasSubjects && shouldShowTour(userProfile.tour_dashboard_with_subjects, userProfile.tour_enabled, 'dashboard-with-subjects')) {
        console.log('[Dashboard] Setting tour to: dashboard-with-subjects');
        setActiveTourSection('dashboard-with-subjects');
      } else {
        setActiveTourSection(null);
      }
    }
  }, [userProfile, hasChildren]);

  // Check if we're returning from add subject page and refresh profile
  useEffect(() => {
    const fromAddSubject = location.state?.fromAddSubject;
    if (fromAddSubject) {
      refreshProfile();
      // Clear the state to prevent future refreshes
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refreshProfile]);

  // Stable callback references for tour handlers
  const handleTourComplete = useCallback(() => {
    console.log('[Dashboard] handleTourComplete called');
    setActiveTourSection(null);
    refreshProfile();
  }, [refreshProfile]);

  const handleTourSkip = useCallback(() => {
    console.log('[Dashboard] handleTourSkip called');
    setActiveTourSection(null);
    refreshProfile();
  }, [refreshProfile]);

  // Core helper: start a lesson with a subject in a specific mode
  const startWithMode = async (subject: EnrolledSubject, mode: LessonMode) => {
    if (mode === 'voice' && VOICE_MODE_ENABLED) {
      await startLessonWithSubject(subject);
    } else {
      await startTextLesson(subject);
    }
  };

  // Initiate lesson for a given subject — respects preferred class type
  const initiateLesson = (subject: EnrolledSubject) => {
    if (!isActive) {
      setTrialExpiredFeature('Starting lessons');
      return;
    }
    setSelectedSubjectForLesson(subject);
    if (!VOICE_MODE_ENABLED) {
      startTextLesson(subject);
      return;
    }
    const preferred = userProfile?.preferred_class_type as LessonMode | null | undefined;
    if (preferred) {
      startWithMode(subject, preferred);
    } else {
      setShowModeSelection(true);
    }
  };

  const handleStartLesson = async () => {
    if (!isActive) {
      setTrialExpiredFeature('Starting lessons');
      return;
    }

    const activeSubjects = enrolledSubjects.filter(s => !s.is_archived);

    if (activeSubjects.length === 0) {
      if (enrolledSubjects.length > 0) {
        setIsCourseLimitModalOpen(true);
      } else {
        setIsNoSubjectsModalOpen(true);
      }
      return;
    }

    if (activeSubjects.length === 1) {
      initiateLesson(activeSubjects[0]);
    } else {
      setIsSubjectModalOpen(true);
    }
  };

  // Called after mode selection when user has no preferred type set
  const handleModeSelection = async (mode: LessonMode) => {
    setShowModeSelection(false);

    if (!selectedSubjectForLesson) {
      console.error('No subject selected for lesson');
      return;
    }

    // Ask user if they want to set this as their default (only when no preference is set)
    setPendingModeForLesson(mode);
    setShowSetDefaultModal(true);
  };

  const handleSetDefault = async (setAsDefault: boolean) => {
    setShowSetDefaultModal(false);
    const mode = pendingModeForLesson;
    setPendingModeForLesson(null);
    if (!mode || !selectedSubjectForLesson) return;

    if (setAsDefault) {
      try {
        await profileAPI.updateProfile({ preferred_class_type: mode });
        await refreshProfile();
      } catch (err) {
        console.error('Failed to set default lesson mode:', err);
      }
    }
    await startWithMode(selectedSubjectForLesson, mode);
  };

  const handleDeleteSubject = async () => {
    if (!deleteSubjectModal) return;
    setIsDeletingSubject(true);
    try {
      await profileAPI.removeSubject(deleteSubjectModal.id);
      await refreshProfile();
      setDeleteSubjectModal(null);
    } catch (err) {
      console.error('Failed to delete subject:', err);
      alert('Failed to delete subject. Please try again.');
    } finally {
      setIsDeletingSubject(false);
    }
  };

  const handleUnarchiveSubject = async (subjectId: string) => {
    try {
      await profileAPI.unarchiveSubject(subjectId);
      await refreshProfile();
      setArchivedSubject(null);
    } catch (err: any) {
      console.error('Failed to unarchive subject:', err);
      alert(err?.message || 'Failed to restore subject. You may need to delete an active subject first or upgrade your plan.');
    }
  };

  const startTextLesson = async (selectedSubject: EnrolledSubject) => {
    setIsStartingLesson(true);
    try {
      const response: StartLessonResponse = await lessonAPI.startLesson(selectedSubject.id);

      // Navigate to lesson page with text mode
      navigate('/lesson', {
        state: {
          lessonData: response,
          lessonMode: 'text' as LessonMode
        }
      });
    } catch (error: any) {
      console.error('Failed to start text lesson:', error);

      if (error.message.includes('404')) {
        alert('No course available for this subject. Please contact support.');
      } else if (error.message.includes('403')) {
        alert('You don\'t have access to this subject. Please contact support.');
      } else {
        alert('Failed to start lesson. Please try again.');
      }
    } finally {
      setIsStartingLesson(false);
    }
  };

  const startLessonWithSubject = async (selectedSubject: EnrolledSubject) => {
    setIsStartingLesson(true);
    try {
      // Check microphone and speaker availability
      let voiceAvailable = true;
      try {
        await checkAudioDevices(selectedSubject);
      } catch (error) {
        // If audio devices are not available, the modal is already shown with the selected subject
        // Don't proceed to lesson - wait for user to choose "Use Text Instead" or "Retry"
        setIsStartingLesson(false);
        return;
      }

      const response: StartLessonResponse = await lessonAPI.startLesson(selectedSubject.id);

      // Navigate to lesson page with voice mode
      navigate('/lesson', {
        state: {
          lessonData: response,
          lessonMode: 'voice' as LessonMode
        }
      });
    } catch (error: any) {
      console.error('Failed to start lesson:', error);

      if (error.message === 'AUDIO_DEVICE_CHECK_FAILED') {
        // Audio device check failed - user was already shown the modal
        return;
      }

      if (error.message.includes('404')) {
        alert('No course available for this subject. Please contact support.');
      } else if (error.message.includes('403')) {
        alert('You don\'t have access to this subject. Please contact support.');
      } else {
        alert('Failed to start lesson. Please try again.');
      }
    } finally {
      setIsStartingLesson(false);
    }
  };

  const handleAddSubject = () => {
    if (!isActive) {
      setTrialExpiredFeature('Adding subjects');
      return;
    }
    if (courseLimit > 0 && enrolledSubjects.filter(s => !s.is_archived).length >= courseLimit) {
      setIsCourseLimitModalOpen(true);
      return;
    }
    navigate('/dashboard/add-subject');
  };

  // Check for microphone and speaker availability
  const checkAudioDevices = async (selectedSubject: EnrolledSubject): Promise<void> => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showAudioDeviceModal('both', 'Your browser doesn\'t support voice lessons. Please use a modern browser like Chrome, Firefox, or Safari to enable voice-based learning.', selectedSubject);
        throw new Error('AUDIO_DEVICE_CHECK_FAILED');
      }

      // Check microphone access
      let microphoneStream: MediaStream | null = null;
      try {
        microphoneStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false
        });
      } catch (micError) {
        let message = 'Voice lessons require microphone access to work properly.';
        
        if (micError instanceof Error) {
          switch (micError.name) {
            case 'NotFoundError':
              message = 'No microphone was detected on your device.\n\nTo use voice lessons, you need to:\n• Connect a microphone or headset to your device\n• Make sure your device has microphone capabilities\n\nOnce you have a microphone connected, try starting the lesson again.';
              break;
            case 'NotAllowedError':
              message = 'Microphone access was denied by your browser.\n\nTo fix this:\n• Look for a microphone icon in your browser\'s address bar and click it\n• Select "Always allow" for microphone access\n• Or check your browser settings under Privacy & Security\n\nAfter granting permission, try starting the lesson again.';
              break;
            case 'NotReadableError':
              message = 'Your microphone is currently being used by another application.\n\nPlease:\n• Close other applications that might be using the microphone\n• Check if other browser tabs are using the microphone\n• Try restarting your browser if the issue persists\n\nThen try starting the lesson again.';
              break;
            default:
              message = 'There was a problem accessing your microphone.\n\nPlease:\n• Check that your microphone is properly connected\n• Make sure no other applications are using it\n• Try refreshing the page\n• Check your browser\'s microphone permissions\n\nIf the problem persists, you can continue with text-based lessons.';
          }
        }
        
        showAudioDeviceModal('microphone', message, selectedSubject);
        throw new Error('AUDIO_DEVICE_CHECK_FAILED');
      }

      // Check speaker/audio output capability
      try {
        // Create a silent audio context to test speaker availability
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        await audioContext.close();
      } catch (speakerError) {
        // If microphone stream was created, clean it up
        if (microphoneStream) {
          microphoneStream.getTracks().forEach(track => track.stop());
        }
        
        showAudioDeviceModal('speaker', 'Voice lessons require working speakers or headphones to hear the AI tutor.\n\nPlease:\n• Check that your speakers or headphones are properly connected\n• Make sure your device volume is turned up\n• Test your audio with another application\n\nOnce your audio is working, try starting the lesson again.', selectedSubject);
        throw new Error('AUDIO_DEVICE_CHECK_FAILED');
      }

      // Clean up microphone stream since we were just testing
      if (microphoneStream) {
        microphoneStream.getTracks().forEach(track => track.stop());
      }

      // Both devices are available - lesson can proceed
      console.log('Audio devices check passed - microphone and speakers are available');
      
    } catch (error) {
      if (error instanceof Error && error.message === 'AUDIO_DEVICE_CHECK_FAILED') {
        throw error;
      }
      
      // Unexpected error during audio device check
      console.error('Unexpected error during audio device check:', error);
      showAudioDeviceModal('both', 'There was an unexpected error checking your audio devices.\n\nPlease:\n• Refresh the page and try again\n• Make sure your microphone and speakers are connected\n• Check your browser permissions\n\nIf the problem continues, you can use text-based lessons instead.', selectedSubject);
      throw new Error('AUDIO_DEVICE_CHECK_FAILED');
    }
  };

  const showAudioDeviceModal = (type: 'microphone' | 'speaker' | 'both', message: string, selectedSubject: EnrolledSubject) => {
    setAudioDeviceModal({
      show: true,
      type,
      message,
      selectedSubject
    });
  };

  const handleAudioDeviceModalAction = async () => {
    const selectedSubject = audioDeviceModal.selectedSubject;
    setAudioDeviceModal({ show: false, type: 'microphone', message: '', selectedSubject: undefined });
    
    if (selectedSubject) {
      // Retry the lesson start with the same subject
      await startLessonWithSubject(selectedSubject);
    }
  };

  const handleUseTextLessons = async () => {
    const selectedSubject = audioDeviceModal.selectedSubject;
    setAudioDeviceModal({ show: false, type: 'microphone', message: '', selectedSubject: undefined });

    if (!selectedSubject) {
      console.error('No selected subject found for text lesson');
      return;
    }

    setIsStartingLesson(true);
    try {
      const response: StartLessonResponse = await lessonAPI.startLesson(selectedSubject.id);

      // Navigate to lesson page with text mode
      navigate('/lesson', {
        state: {
          lessonData: response,
          lessonMode: 'text' as LessonMode
        }
      });
    } catch (error: any) {
      console.error('Failed to start text lesson:', error);

      if (error.message.includes('404')) {
        alert('No course available for this subject. Please contact support.');
      } else if (error.message.includes('403')) {
        alert('You don\'t have access to this subject. Please contact support.');
      } else {
        alert('Failed to start lesson. Please try again.');
      }
    } finally {
      setIsStartingLesson(false);
    }
  };

  // Show loading state if profile is not yet available
  if (!userProfile) {
    return (
      <Layout>
        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
            <div className="flex justify-center items-center h-64">
              <div className="text-white text-lg">Loading your profile...</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Subject Selection Modal
  const SubjectSelectionModal = () => {
    if (!isSubjectModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#111827] rounded-lg p-6 max-w-2xl w-full mx-4 border border-gray-600">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white text-xl font-bold">Select a Subject</h2>
            <button
              onClick={() => setIsSubjectModalOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-gray-300 text-sm mb-4">Choose which subject you'd like to start a lesson for:</p>
          
          <div className="grid grid-cols-[repeat(auto-fit,minmax(158px,1fr))] gap-3">
            {enrolledSubjects.filter(s => !s.is_archived).map((subject) => {
              const Icon = iconMap[subject.icon as keyof typeof iconMap];
              return (
                <div
                  key={subject.id}
                  onClick={() => {
                    if (!isStartingLesson) {
                      setIsSubjectModalOpen(false);
                      initiateLesson(subject);
                    }
                  }}
                  className={`cursor-pointer ${isStartingLesson ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <SubjectCard
                    subject={subject}
                    icon={<Icon className="w-6 h-6" />}
                  />
                </div>
              );
            })}
          </div>
          
          {isStartingLesson && (
            <div className="flex items-center justify-center gap-2 mt-4 text-white">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Starting lesson...</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // No Subjects Modal
  const NoSubjectsModal = () => {
    if (!isNoSubjectsModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#111827] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-600">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white text-xl font-bold">No Subjects Available</h2>
            <button
              onClick={() => setIsNoSubjectsModalOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-gray-300 text-sm mb-6">
            You need to add a subject before starting a lesson. Would you like to add a subject now?
          </p>
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setIsNoSubjectsModalOpen(false)}
              className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => {
                setIsNoSubjectsModalOpen(false);
                navigate('/dashboard/add-subject');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Subject
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Course Limit Modal
  const CourseLimitModal = () => {
    if (!isCourseLimitModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#111827] rounded-lg p-6 max-w-md w-full mx-4 border border-yellow-600">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white text-xl font-bold">Course Limit Reached</h2>
            <button
              onClick={() => setIsCourseLimitModalOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-gray-300 text-sm mb-6">
            Sorry! You've reached your course limit ({courseLimit} course{courseLimit === 1 ? '' : 's'}) on your current plan. Upgrade to add more courses and keep learning.
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setIsCourseLimitModalOpen(false)}
              className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => {
                setIsCourseLimitModalOpen(false);
                navigate('/settings/subscription');
              }}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded transition-colors"
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Audio Device Modal
  const AudioDeviceModal = () => {
    if (!audioDeviceModal.show) return null;

    const getTitle = () => {
      switch (audioDeviceModal.type) {
        case 'microphone':
          return 'Microphone Required';
        case 'speaker':
          return 'Speakers Required';
        case 'both':
          return 'Audio Devices Required';
        default:
          return 'Audio Device Required';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#111827] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-600">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white text-xl font-bold">{getTitle()}</h2>
            <button
              onClick={() => setAudioDeviceModal({ show: false, type: 'microphone', message: '' })}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <p className="text-gray-300 text-sm mb-6 whitespace-pre-line">
            {audioDeviceModal.message}
          </p>
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={handleUseTextLessons}
              className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors"
            >
              Use Text Lessons
            </button>
            <button
              onClick={handleAudioDeviceModalAction}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Determine account type, defaulting to 'student' if not set
  const accountType = userProfile.account_type || 'student';

  // Render parent dashboard for parent accounts
  if (accountType === 'parent') {
    return (
      <Layout>
        <TrialBanner />
        <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
            <UserProfile />
            <ParentDashboard onChildrenChange={setHasChildren} />
          </div>
        </div>

        {/* Parent Tours */}
        {activeTourSection === 'parent-no-children' && (
          <>
            {console.log('[Dashboard] Rendering WalkthroughTour for parent-no-children')}
            <WalkthroughTour
              section="parent-no-children"
              steps={parentNoChildrenSteps}
              isActive={true}
              onComplete={handleTourComplete}
              onSkip={handleTourSkip}
            />
          </>
        )}

        {activeTourSection === 'parent-with-children' && (
          <>
            {console.log('[Dashboard] Rendering WalkthroughTour for parent-with-children')}
            <WalkthroughTour
              section="parent-with-children"
              steps={parentWithChildrenSteps}
              isActive={true}
              onComplete={handleTourComplete}
              onSkip={handleTourSkip}
            />
          </>
        )}
      </Layout>
    );
  }

  // Render regular dashboard for student and child accounts
  const isChildAccount = accountType === 'child';

  return (
    <Layout>
      <TrialBanner />
      <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-5">
        <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
          <UserProfile />

          {/* Show course limit warning — only count active (non-archived) subjects */}
          {userProfile?.subjects && courseLimit > 0 && userProfile.subjects.filter((s: any) => !s.is_archived).length >= courseLimit && (
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-6 flex items-center justify-between gap-4">
              <p className="text-yellow-200 text-sm">
                You've reached your course limit ({courseLimit} course{courseLimit === 1 ? '' : 's'}).
              </p>
              <button
                onClick={() => navigate('/settings/subscription')}
                className="flex-shrink-0 px-4 py-1.5 bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold rounded-lg transition-colors"
              >
                Upgrade Plan
              </button>
            </div>
          )}

          <SubjectTabs />

          <h3 className="text-white text-lg font-bold leading-tight tracking-[-0.015em] px-4 pb-2 pt-4">
            Enrolled Subjects
          </h3>
          {/* Mobile: horizontal scroll with peek effect, Desktop: grid */}
          <div className="md:hidden overflow-x-auto px-4 pb-4 scrollbar-hide">
            <div className="flex gap-3" style={{ paddingRight: '15%' }}>
              {enrolledSubjects.length > 0 ? (
                enrolledSubjects.map((subject) => {
                  const Icon = iconMap[subject.icon as keyof typeof iconMap];
                  return (
                    <div key={subject.id} className="flex-shrink-0 w-40">
                      <SubjectCard
                        subject={subject}
                        icon={<Icon className="w-6 h-6" />}
                        isArchived={subject.is_archived}
                        onClick={subject.is_archived
                          ? () => setArchivedSubject({ id: subject.id, name: subject.name })
                          : () => initiateLesson(subject)
                        }
                        onDelete={!isChildAccount ? (e) => { e.stopPropagation(); setDeleteSubjectModal({ id: subject.id, name: subject.name }); } : undefined}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="w-full text-text-secondary text-center py-8">
                  {isChildAccount
                    ? 'No subjects yet. Ask your parent to add subjects for you!'
                    : 'No enrolled subjects yet. Add a subject below to get started!'}
                </div>
              )}
            </div>
          </div>
          {/* Desktop grid view */}
          <div className="hidden md:grid grid-cols-[repeat(auto-fit,minmax(158px,1fr))] gap-3 p-4">
            {enrolledSubjects.length > 0 ? (
              enrolledSubjects.map((subject) => {
                const Icon = iconMap[subject.icon as keyof typeof iconMap];
                return (
                  <SubjectCard
                    key={subject.id}
                    subject={subject}
                    icon={<Icon className="w-6 h-6" />}
                    isArchived={subject.is_archived}
                    onClick={subject.is_archived
                      ? () => setArchivedSubject({ id: subject.id, name: subject.name })
                      : () => initiateLesson(subject)
                    }
                    onDelete={!isChildAccount ? (e) => { e.stopPropagation(); setDeleteSubjectModal({ id: subject.id, name: subject.name }); } : undefined}
                  />
                );
              })
            ) : (
              <div className="col-span-full text-text-secondary text-center py-8">
                {isChildAccount
                  ? 'No subjects yet. Ask your parent to add subjects for you!'
                  : 'No enrolled subjects yet. Add a subject below to get started!'}
              </div>
            )}
          </div>

          {/* Hide Add Subject button for child accounts */}
          {!isChildAccount && (
            <div className="flex justify-center mt-8">
              <button
                data-tour="add-subject-button"
                onClick={handleAddSubject}
                className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-6 bg-secondary text-white text-lg font-bold leading-normal tracking-[0.015em] hover:bg-secondary/90 transition-colors gap-2"
              >
                <Plus className="w-5 h-5" />
                Add New Subject
              </button>
            </div>
          )}

          <div className="flex justify-center mt-4">
            <button
              data-tour="start-lesson-button"
              onClick={handleStartLesson}
              disabled={isStartingLesson}
              className="flex min-w-[200px] cursor-pointer items-center justify-center overflow-hidden rounded h-12 px-6 bg-primary text-white text-lg font-bold leading-normal tracking-[0.015em] hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed gap-2"
            >
              {isStartingLesson ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Starting...
                </>
              ) : (
                'Start New Lesson'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Archived Subject Modal */}
      {archivedSubject && (() => {
        const activeCount = enrolledSubjects.filter(s => !s.is_archived).length;
        const hasRoom = courseLimit <= 0 || activeCount < courseLimit;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#111827] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-600">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-white text-xl font-bold">Subject Archived</h2>
                <button onClick={() => setArchivedSubject(null)} className="text-gray-400 hover:text-white transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-gray-300 text-sm mb-2">
                <strong className="text-white">{archivedSubject.name}</strong> is archived because it exceeded your plan's course limit.
              </p>
              {hasRoom ? (
                <p className="text-gray-400 text-xs mb-6">
                  You have a free slot — you can restore this subject now.
                </p>
              ) : (
                <p className="text-gray-400 text-xs mb-6">
                  To restore it, either delete an active subject to free up a slot, or upgrade your plan.
                </p>
              )}
              <div className="flex gap-3 justify-end flex-wrap">
                <button
                  onClick={() => setArchivedSubject(null)}
                  className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors text-sm"
                >
                  Close
                </button>
                {!hasRoom && (
                  <button
                    onClick={() => { setArchivedSubject(null); navigate('/settings/subscription'); }}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded transition-colors text-sm"
                  >
                    Upgrade Plan
                  </button>
                )}
                {hasRoom && (
                  <button
                    onClick={() => handleUnarchiveSubject(archivedSubject.id)}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded transition-colors text-sm"
                  >
                    Restore Subject
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Subject Confirmation Modal */}
      {deleteSubjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#111827] rounded-lg p-6 max-w-md w-full mx-4 border border-red-800">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white text-xl font-bold">Delete Subject?</h2>
              <button onClick={() => setDeleteSubjectModal(null)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-300 text-sm mb-2">
              You are about to permanently delete <strong className="text-white">{deleteSubjectModal.name}</strong>.
            </p>
            <p className="text-red-400 text-xs mb-6">
              This will delete all associated lessons, course data, and progress reports. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteSubjectModal(null)}
                disabled={isDeletingSubject}
                className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSubject}
                disabled={isDeletingSubject}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-semibold rounded transition-colors text-sm disabled:opacity-50"
              >
                {isDeletingSubject ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Default Lesson Mode Modal */}
      {showSetDefaultModal && pendingModeForLesson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#111827] rounded-lg p-6 max-w-sm w-full mx-4 border border-gray-600">
            <h2 className="text-white text-lg font-bold mb-2">Save as your default?</h2>
            <p className="text-gray-300 text-sm mb-6">
              Make <strong className="text-white capitalize">{pendingModeForLesson}</strong> mode your default for future lessons? You can change this anytime in Profile Settings.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => handleSetDefault(false)}
                className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors text-sm"
              >
                Just this once
              </button>
              <button
                onClick={() => handleSetDefault(true)}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded transition-colors text-sm"
              >
                Yes, set as default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Expired Modal */}
      {trialExpiredFeature && (
        <PremiumFeatureLock
          featureName={trialExpiredFeature}
          onUpgrade={() => {
            setTrialExpiredFeature(null);
            navigate('/settings/subscription');
          }}
          onDismiss={() => setTrialExpiredFeature(null)}
        />
      )}

      {/* Subject Selection Modal */}
      <SubjectSelectionModal />

      {/* No Subjects Modal */}
      <NoSubjectsModal />

      {/* Course Limit Modal */}
      <CourseLimitModal />

      {/* Audio Device Modal */}
      <AudioDeviceModal />

      {/* Mode Selection Modal */}
      {showModeSelection && (
        <ModeSelectionModal
          onSelectMode={handleModeSelection}
          onCancel={() => {
            setShowModeSelection(false);
            setSelectedSubjectForLesson(null);
          }}
          audioAvailable={audioAvailable}
          defaultMode={(userProfile?.preferred_class_type as LessonMode | null) || null}
        />
      )}

      {/* Contextual Tour Sections */}
      {activeTourSection === 'dashboard-no-subjects' && (
        <>
          {console.log('[Dashboard] Rendering WalkthroughTour for dashboard-no-subjects')}
          <WalkthroughTour
            section="dashboard-no-subjects"
            steps={dashboardNoSubjectsSteps}
            isActive={true}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        </>
      )}

      {activeTourSection === 'dashboard-with-subjects' && (
        <>
          {console.log('[Dashboard] Rendering WalkthroughTour for dashboard-with-subjects')}
          <WalkthroughTour
            section="dashboard-with-subjects"
            steps={dashboardWithSubjectsSteps}
            isActive={true}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        </>
      )}

      {activeTourSection === 'child-no-subjects' && (
        <>
          {console.log('[Dashboard] Rendering WalkthroughTour for child-no-subjects')}
          <WalkthroughTour
            section="child-no-subjects"
            steps={childNoSubjectsSteps}
            isActive={true}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        </>
      )}

      {activeTourSection === 'child-with-subjects' && (
        <>
          {console.log('[Dashboard] Rendering WalkthroughTour for child-with-subjects')}
          <WalkthroughTour
            section="child-with-subjects"
            steps={childWithSubjectsSteps}
            isActive={true}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        </>
      )}
    </Layout>
  );
}
